import type { Decision } from './decide';
import { domainOf, type DomainId, type IntentId } from './taxonomy';

/** What the router names for a question, and how it got there. */
export type Route = {
  intent: IntentId;
  domain: DomainId;
  confidence: number;
  /** `rule`: a keyword; `model`: a sure intent; `domain`: only the topic; `unknown`: nothing. */
  via: 'rule' | 'model' | 'domain' | 'unknown';
};

/** The situation a keyword names, and how likely the model finds that situation for the sentence. */
export type Keyword = { intent: IntentId; prob: number };

export type CombinePolicy = {
  /** How sure the model must be of an intent to win over a keyword. */
  trust: number;
  /** How sure it must be of a topic (without an intent) to overrule a keyword of another topic. */
  topicTrust: number;
  /** A keyword whose intent the model finds less likely than this is vetoed... */
  veto: number;
  /** ...when the model has a best guess of its own of at least this much. */
  vetoBest: number;
  /** A keyword is a sign the question is in scope: to answer "fuera" against one, the model must be this sure. */
  fueraTrust: number;
};

/**
 * The model reads the whole sentence, so when it is very sure it wins over a keyword (a refusal that
 * mentions "oferta" is not a request for an offer). When it is sure of the topic but not of the intent,
 * it still overrules a keyword that points to another topic, with the general answer of its topic. It also
 * vetoes a keyword it finds almost impossible ("no quiero volver a ver esa oferta" is not about clients who
 * did not return) and follows its own best guess. In every other case the keyword, which never misreads the
 * wording it was written for, answers; and without a keyword the model decides.
 */
export function combine(keyword: Keyword | null, model: Decision, { trust, topicTrust, veto, vetoBest, fueraTrust }: CombinePolicy): Route {
  // Refusing ("fuera") a sentence that a keyword recognizes needs much more certainty than any other answer.
  const sure = (intent: IntentId, domain: DomainId, confidence: number) => !keyword || (intent !== 'fuera' && domain !== 'fuera') || confidence >= fueraTrust;
  if (model.via === 'intent' && model.confidence >= trust && sure(model.intent, model.domain, model.confidence)) {
    return { intent: model.intent, domain: model.domain, confidence: model.confidence, via: 'model' };
  }
  if (keyword && keyword.prob < veto && model.best >= vetoBest && keyword.intent !== model.top && sure(model.top, domainOf(model.top), model.best)) {
    return { intent: model.top, domain: domainOf(model.top), confidence: model.best, via: 'model' };
  }
  if (keyword && model.via === 'domain' && model.confidence >= topicTrust && domainOf(keyword.intent) !== model.domain && sure(model.intent, model.domain, model.confidence)) {
    return { intent: model.intent, domain: model.domain, confidence: model.confidence, via: 'domain' };
  }
  if (keyword) return { intent: keyword.intent, domain: domainOf(keyword.intent), confidence: 1, via: 'rule' };
  return { intent: model.intent, domain: model.domain, confidence: model.confidence, via: model.via === 'intent' ? 'model' : model.via };
}
